import {
    ref,
    uploadBytes,
    uploadString,
    getDownloadURL,
    deleteObject,
} from "firebase/storage";
import { getFirebaseStorage } from "./firebase";
import { log } from "../utils/logger";

/**
 * Files live in Storage, and a ticket only holds their URLs.
 *
 * Screenshots and mockups used to be embedded in the Firestore document as
 * base64 data URLs. Firestore caps a document at 1 MiB, and a full-tab retina
 * PNG is routinely past that, so the save failed — and the ticket then
 * vanished on the next snapshot.
 *
 * Everything for a ticket sits under one prefix so the security rules can
 * scope it and deletion can find it.
 */
const ticketPath = (projectId: string, feedbackId: string, name: string) =>
    `feedback/${encodeURIComponent(projectId)}/${feedbackId}/${name}`;

/** Upload an image held as a data URL (screenshot, annotated shot, mockup). */
export const uploadImageDataUrl = async (
    dataUrl: string,
    projectId: string,
    feedbackId: string,
    kind: "screenshot" | "mockup",
): Promise<string> => {
    const type = /^data:([^;,]+)/.exec(dataUrl)?.[1] ?? "image/png";
    const ext = type.split("/")[1]?.replace("jpeg", "jpg") ?? "png";
    const fileRef = ref(
        getFirebaseStorage(),
        ticketPath(projectId, feedbackId, `${kind}-${Date.now()}.${ext}`),
    );
    const snapshot = await uploadString(fileRef, dataUrl, "data_url", {
        contentType: type,
    });
    return getDownloadURL(snapshot.ref);
};

/** Upload a screen recording, keeping whatever container the browser made. */
export const uploadScreenRecording = async (
    blob: Blob,
    projectId: string,
    feedbackId: string,
): Promise<string> => {
    // Safari records MP4, Chromium and Firefox WebM.
    const type = blob.type || "video/webm";
    const ext = type.includes("mp4") ? "mp4" : "webm";
    const fileRef = ref(
        getFirebaseStorage(),
        ticketPath(projectId, feedbackId, `recording-${Date.now()}.${ext}`),
    );
    const snapshot = await uploadBytes(fileRef, blob, { contentType: type });
    const url = await getDownloadURL(snapshot.ref);
    log("Screen recording uploaded");
    return url;
};

/** True for a URL that points into this app's Storage bucket. */
export const isStorageUrl = (url: string | undefined | null): url is string =>
    !!url && url.includes("firebasestorage");

/**
 * Delete a stored file by its download URL. Missing files are fine — the goal
 * is only that it's gone.
 */
export const deleteStoredFile = async (downloadURL: string): Promise<void> => {
    try {
        // https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?…
        const match = new URL(downloadURL).pathname.match(/\/o\/(.+)$/);
        if (!match?.[1]) return;
        await deleteObject(
            ref(getFirebaseStorage(), decodeURIComponent(match[1])),
        );
    } catch (error: any) {
        if (error?.code !== "storage/object-not-found") {
            console.error("[markup] Failed to delete stored file");
        }
    }
};

/** @deprecated Use `deleteStoredFile`. */
export const deleteScreenRecording = deleteStoredFile;
